// Inline SVG helpers. Colors come from CSS classes (tools.css), not inline values.
import { el } from './dom.js';

const NS = 'http://www.w3.org/2000/svg';

export function s(tag, attrs = {}, ...children) {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'text') node.textContent = v;
    else node.setAttribute(k, String(v));
  }
  for (const c of children.flat(Infinity)) {
    if (c === null || c === undefined || c === false) continue;
    node.append(c instanceof Node ? c : document.createTextNode(String(c)));
  }
  return node;
}

/** Root <svg> with viewBox, role=img and aria-label. */
export function svgRoot(width, height, label, cls = '') {
  return s('svg', {
    viewBox: `0 0 ${width} ${height}`,
    class: 'tool-svg ' + cls,
    role: 'img',
    'aria-label': label,
    preserveAspectRatio: 'xMidYMid meet',
  });
}

/** Wrapper figure in media width with text alternative. */
export function Figure(svg, { caption, alt } = {}) {
  return el(
    'figure',
    { class: 'tool-figure' },
    svg,
    alt ? el('div', { class: 'visually-hidden' }, alt) : null,
    caption ? el('figcaption', { class: 'tool-figure__caption' }, caption) : null,
  );
}

/** "Nice" tick values for an axis between min and max. */
export function ticks(min, max, count = 5) {
  if (!(max > min)) return [min];
  const span = max - min;
  const raw = span / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const out = [];
  const start = Math.ceil(min / step - 1e-9) * step;
  for (let v = start; v <= max + step * 1e-9; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

export function scaleLinear([d0, d1], [r0, r1]) {
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0);
  return (v) => r0 + (v - d0) * k;
}

export function scaleLog([d0, d1], [r0, r1]) {
  const l0 = Math.log10(d0);
  const l1 = Math.log10(d1);
  return (v) => r0 + ((Math.log10(v) - l0) / (l1 - l0)) * (r1 - r0);
}

/**
 * Horizontal bar chart. rows: [{ label, value, valueLabel, cls }]
 * Wide: label left, bar, value right. Below `stackBelow` px: label above the bar.
 */
export function barChart(rows, { label, width = 720, rowHeight = 34, labelWidth = 230, valueWidth = 130, max, stackBelow = 560 } = {}) {
  const top = Math.max(max || 0, ...rows.map((r) => r.value), 0) || 1;
  const pad = 8;
  if (width < stackBelow) {
    const rh = 50;
    const height = rows.length * rh + pad;
    const svg = svgRoot(width, height, label, 'tool-svg--bars');
    // Room for the longest value label right of the longest bar (13 px mono ≈ 7.8 px per char).
    const longest = Math.max(0, ...rows.map((r) => String(r.valueLabel ?? r.value).length));
    const x1 = width - Math.max(76, Math.ceil(longest * 7.8) + 12);
    const scale = scaleLinear([0, top], [0, x1]);
    rows.forEach((r, i) => {
      const y = pad + i * rh;
      svg.append(
        s('text', { x: 0, y: y + 10, class: 'svg-label', 'dominant-baseline': 'central' }, truncate(r.label, Math.floor(width / 7.6))),
        s('rect', { x: 0, y: y + 22, width: Math.max(r.value > 0 ? 2 : 0, scale(r.value)), height: 16, rx: 3, class: 'svg-bar ' + (r.cls || '') }),
        s('text', { x: scale(r.value) + 8, y: y + 30, class: 'svg-value', 'dominant-baseline': 'central' }, r.valueLabel ?? String(r.value)),
      );
    });
    return svg;
  }
  const height = Math.max(rowHeight, rows.length * rowHeight) + pad * 2;
  const svg = svgRoot(width, height, label, 'tool-svg--bars');
  const x0 = labelWidth;
  const x1 = width - valueWidth;
  const scale = scaleLinear([0, top], [x0, x1]);
  rows.forEach((r, i) => {
    const y = pad + i * rowHeight;
    const barH = rowHeight * 0.56;
    const by = y + (rowHeight - barH) / 2;
    svg.append(
      s('text', { x: x0 - 12, y: y + rowHeight / 2, class: 'svg-label', 'text-anchor': 'end', 'dominant-baseline': 'central' }, truncate(r.label, Math.floor((labelWidth - 12) / 7.6))),
      s('rect', { x: x0, y: by, width: Math.max(r.value > 0 ? 2 : 0, scale(r.value) - x0), height: barH, rx: 3, class: 'svg-bar ' + (r.cls || '') }),
      s('text', { x: scale(r.value) + 8, y: y + rowHeight / 2, class: 'svg-value', 'dominant-baseline': 'central' }, r.valueLabel ?? String(r.value)),
    );
  });
  svg.append(s('line', { x1: x0, x2: x0, y1: pad / 2, y2: height - pad / 2, class: 'svg-axis' }));
  return svg;
}

export function truncate(text, max) {
  const t = String(text);
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

/** Plot frame with axes and grid. Returns { svg, x, y, plot: {x0,x1,y0,y1} } */
export function plotFrame({ width = 720, height = 320, label, xDomain, yDomain, xTicks, yTicks, xFormat = String, yFormat = String, xLabel, yLabel, xLog = false, margin = {} }) {
  const m = { top: 16, right: 16, bottom: 44, left: 56, ...margin };
  const svg = svgRoot(width, height, label, 'tool-svg--plot');
  const plot = { x0: m.left, x1: width - m.right, y0: height - m.bottom, y1: m.top };
  const x = (xLog ? scaleLog : scaleLinear)(xDomain, [plot.x0, plot.x1]);
  const y = scaleLinear(yDomain, [plot.y0, plot.y1]);
  const grid = s('g', { class: 'svg-grid' });
  (yTicks || ticks(yDomain[0], yDomain[1], 5)).forEach((t) => {
    grid.append(s('line', { x1: plot.x0, x2: plot.x1, y1: y(t), y2: y(t), class: 'svg-gridline' }));
    grid.append(s('text', { x: plot.x0 - 8, y: y(t), class: 'svg-tick', 'text-anchor': 'end', 'dominant-baseline': 'central' }, yFormat(t)));
  });
  (xTicks || ticks(xDomain[0], xDomain[1], 6)).forEach((t) => {
    grid.append(s('line', { x1: x(t), x2: x(t), y1: plot.y0, y2: plot.y0 + 5, class: 'svg-axis' }));
    grid.append(s('text', { x: x(t), y: plot.y0 + 18, class: 'svg-tick', 'text-anchor': 'middle' }, xFormat(t)));
  });
  grid.append(s('line', { x1: plot.x0, x2: plot.x1, y1: plot.y0, y2: plot.y0, class: 'svg-axis' }));
  grid.append(s('line', { x1: plot.x0, x2: plot.x0, y1: plot.y0, y2: plot.y1, class: 'svg-axis' }));
  if (xLabel) grid.append(s('text', { x: (plot.x0 + plot.x1) / 2, y: height - 6, class: 'svg-axislabel', 'text-anchor': 'middle' }, xLabel));
  if (yLabel) grid.append(s('text', { x: 12, y: (plot.y0 + plot.y1) / 2, class: 'svg-axislabel', 'text-anchor': 'middle', transform: `rotate(-90 12 ${(plot.y0 + plot.y1) / 2})` }, yLabel));
  svg.append(grid);
  return { svg, x, y, plot };
}

/** Polyline path from points [[x,y],…] (already scaled). */
export function pathFrom(points) {
  return points.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
}

/** Legend row in HTML (accessible, wraps on mobile). items: [{ label, cls }] */
export function Legend(items) {
  return el(
    'ul',
    { class: 'tool-legend' },
    items.map((i) => el('li', {}, el('span', { class: 'tool-legend__swatch ' + (i.cls || ''), 'aria-hidden': 'true' }), i.label)),
  );
}

/**
 * Render a chart at the real pixel width of `host`, so text keeps its CSS size
 * on mobile. render(width) must return an SVG node; re-renders on resize.
 * Returns { update() } to redraw after data changes.
 */
export function responsiveChart(host, render, { min = 280, max = 1400 } = {}) {
  let last = 0;
  const draw = (force) => {
    const w = Math.round(Math.min(max, Math.max(min, host.clientWidth || 720)));
    if (!force && w === last) return;
    last = w;
    const node = render(w);
    host.replaceChildren(...(node ? [node] : []));
  };
  if (typeof ResizeObserver !== 'undefined') {
    let t;
    new ResizeObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => draw(false), 60);
    }).observe(host);
  }
  return { update: () => draw(true) };
}
